import React, { useEffect, useRef } from 'react';
import { direccionParaMaps } from '../../utils/direccionMaps';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { estiloMapa, LIMITES_AMBA, CENTRO_AMBA } from './estiloMapa';

// Mapa base (7-oct-2026) con Leaflet. 12-oct-2026: queda dentro de AMBA (no se ve el mundo
// entero) y el fondo es un mapa vectorial de OpenFreeMap (Liberty/Positron, se elige en
// Configuración), con el OpenStreetMap clásico de respaldo. En modo oscuro el fondo se invierte.
const OSM = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const ATTR_OSM = '© OpenStreetMap';
const ATTR_OFM = '<a href="https://openfreemap.org" target="_blank" rel="noreferrer">OpenFreeMap</a> © OpenStreetMap';

function capaClasica() {
    return L.tileLayer(process.env.REACT_APP_MAP_TILES || OSM, { attribution: process.env.REACT_APP_MAP_ATTR || ATTR_OSM, maxZoom: 19 });
}

// El mapa vectorial se carga aparte (pesa) y solo si se usa
async function capaVectorial(estilo) {
    const [{ maplibreGL }] = await Promise.all([
        import('@maplibre/maplibre-gl-leaflet'),
        import('maplibre-gl/dist/maplibre-gl.css'),
    ]);
    const capa = maplibreGL({ style: `https://tiles.openfreemap.org/styles/${estilo}`, attribution: ATTR_OFM });
    // 23-oct-2026 (testeo U13): la librería deja un cuadro de animación pendiente que, si el mapa ya se
    // cerró (saliste del Mapa mientras acomodaba el zoom), pide getZoom de un mapa que no existe. Misma
    // lógica que la original, pero sin hacer nada si la capa ya no está en un mapa.
    capa._transitionEnd = function () {
        L.Util.requestAnimFrame(function () {
            if (!this._map || !this._glMap) return;
            const zoom = this._map.getZoom();
            const center = this._map.getCenter();
            const offset = this._map.latLngToContainerPoint(this._map.getBounds().getNorthWest());
            this._resizeContainer();
            L.DomUtil.setTransform(this._glMap._actualCanvas, offset, 1);
            this._glMap.once('moveend', L.Util.bind(function () { this._zoomEnd(); }, this));
            this._glMap.jumpTo({ center, zoom: zoom - 1 });
        }, this);
    };
    const zoomEnd = capa._zoomEnd;
    capa._zoomEnd = function () { if (this._map) zoomEnd.call(this); };
    return capa;
}

// puntos: [{ id, lat, lng, color, etiqueta?, titulo?, tituloFijo?, hueco? (sin agendar) }]
// lineas: [[lat, lng], ...] o [{ coords, color }] · circulos: [{ lat, lng, km, color }] · punto.apagado: gris y chico (12-oct-2026)
// agrupar (2-nov-2026, Mapa → Equipos): de lejos, los puntos pegados se juntan en una burbuja con cuántos son y el
// color del que más pide atención (el de mayor p.z); al tocarla se acerca. Desde zoom 15 se ven todos sueltos.
const ZOOM_SUELTOS = 15, CELDA = 46;
function grupos(mapa, pts, seleccionado) {
    const z = mapa.getZoom();
    if (z >= ZOOM_SUELTOS) return pts.map(p => [p]);
    const celdas = new Map(), sueltos = [];
    pts.forEach(p => {
        if (p.id === seleccionado) { sueltos.push([p]); return; }
        const q = mapa.project([p.lat, p.lng], z);
        const k = `${Math.floor(q.x / CELDA)}:${Math.floor(q.y / CELDA)}`;
        if (!celdas.has(k)) celdas.set(k, []);
        celdas.get(k).push(p);
    });
    return [...celdas.values(), ...sueltos];
}

export default function MapaLeaflet({ puntos = [], seleccionado = null, onClickPunto, onClickMapa, ajustarKey, className = '', lineas = null, circulos = null, acercar = false, agrupar = false }) {
    const divRef = useRef(null);
    const mapaRef = useRef(null);
    const capaRef = useRef(null);
    const extrasRef = useRef(null);
    const fondoRef = useRef(null);
    const clickPuntoRef = useRef(onClickPunto);
    const clickMapaRef = useRef(onClickMapa);
    clickPuntoRef.current = onClickPunto;
    clickMapaRef.current = onClickMapa;

    useEffect(() => {
        const mapa = L.map(divRef.current, {
            zoomControl: true, attributionControl: true,
            minZoom: 9, maxBounds: LIMITES_AMBA, maxBoundsViscosity: 0.9,
        }).setView(CENTRO_AMBA, 11);
        capaRef.current = L.layerGroup().addTo(mapa);
        extrasRef.current = L.layerGroup().addTo(mapa);
        mapa.on('click', (e) => clickMapaRef.current && clickMapaRef.current(e.latlng));
        mapa.on('zoomend', () => dibujarRef.current && dibujarRef.current(true));
        mapaRef.current = mapa;

        let vivo = true;
        const ponerFondo = async (estilo) => {
            let capa;
            if (estilo !== 'clasico') {
                try { capa = await capaVectorial(estilo); } catch { capa = null; }   // sin WebGL o sin red: clásico
            }
            if (!vivo) return;
            if (fondoRef.current) mapa.removeLayer(fondoRef.current);
            try {
                fondoRef.current = (capa || capaClasica()).addTo(mapa);
            } catch {   // el navegador no puede dibujar mapas vectoriales (sin WebGL)
                fondoRef.current = capaClasica().addTo(mapa);
                return;
            }
            fondoRef.current.bringToBack && fondoRef.current.bringToBack();
            // Si el estilo vectorial no termina de cargar (sin conexión con el servidor de mapas), clásico
            const gl = capa && capa.getMaplibreMap && capa.getMaplibreMap();
            if (gl) {
                setTimeout(() => {
                    if (vivo && fondoRef.current === capa && !gl.isStyleLoaded()) {
                        mapa.removeLayer(capa);
                        fondoRef.current = capaClasica().addTo(mapa);
                    }
                }, 8000);
            }
        };
        ponerFondo(estiloMapa());
        const alCambiar = (e) => ponerFondo(e.detail);
        window.addEventListener('mapa-estilo', alCambiar);

        // El contenedor puede cambiar de tamaño (menú lateral, pestañas)
        const ro = new ResizeObserver(() => mapa.invalidateSize());
        ro.observe(divRef.current);
        // Al salir (23-oct-2026, testeo U13): cortar animaciones y sacar el fondo vectorial antes de
        // destruir el mapa; si no, una animación o el fondo que seguía cargando pedían getZoom de un mapa
        // que ya no existía (error "getZoom of null" al salir del Mapa antes de que cargue).
        return () => {
            vivo = false; window.removeEventListener('mapa-estilo', alCambiar); ro.disconnect();
            try { mapa.stop(); if (fondoRef.current) mapa.removeLayer(fondoRef.current); } catch { /* ya no estaba */ }
            fondoRef.current = null; mapaRef.current = null;
            try { mapa.remove(); } catch { /* */ }
        };
    }, []);

    // Dibujar puntos (con agrupar: de nuevo en cada zoom)
    const dibujarRef = useRef(null);
    const dibujar = (porZoom = false) => {
        const capa = capaRef.current;
        const mapa = mapaRef.current;
        if (!capa || !mapa) return;
        if (porZoom && !agrupar) return;
        capa.clearLayers();
        const conPos = puntos.filter(p => p.lat != null && p.lng != null);
        const sueltos = [];
        (agrupar ? grupos(mapa, conPos, seleccionado) : conPos.map(p => [p])).forEach(g => {
            if (g.length === 1) { sueltos.push(g[0]); return; }
            const peor = g.reduce((a, b) => ((b.z || 0) > (a.z || 0) ? b : a), g[0]);
            const tam = g.length >= 20 ? 44 : g.length >= 8 ? 38 : 32;
            const lat = g.reduce((t, p) => t + p.lat, 0) / g.length, lng = g.reduce((t, p) => t + p.lng, 0) / g.length;
            const m = L.marker([lat, lng], {
                icon: L.divIcon({ html: `<div class="dlt-pin dlt-pin-grupo" style="--c:${peor.color || '#2563EB'}"><span>${g.length}</span></div>`,
                    className: 'dlt-pin-wrap', iconSize: [tam, tam], iconAnchor: [tam / 2, tam / 2] }),
                zIndexOffset: (peor.z || 0) + 50, keyboard: false,
            });
            m.bindTooltip(`${g.length} lugares · tocá para acercarte`, { direction: 'top', offset: [0, -tam / 2], className: 'dlt-tip', opacity: 1 });
            m.on('click', (e) => {
                L.DomEvent.stopPropagation(e);
                mapa.fitBounds(L.latLngBounds(g.map(p => [p.lat, p.lng])).pad(0.3), { maxZoom: ZOOM_SUELTOS + 1 });
            });
            capa.addLayer(m);
        });
        sueltos.forEach(p => {
            const sel = seleccionado === p.id;
            const html = `<div class="dlt-pin${sel ? ' dlt-pin-sel' : ''}${p.hueco ? ' dlt-pin-hueco' : ''}" style="--c:${p.color || '#2563EB'}">${p.etiqueta ? `<span>${p.etiqueta}</span>` : ''}</div>`;
            const tam = sel ? 34 : p.apagado ? 12 : (p.etiqueta ? 28 : p.hueco ? 22 : 20);
            const m = L.marker([p.lat, p.lng], {
                icon: L.divIcon({ html, className: 'dlt-pin-wrap', iconSize: [tam, tam], iconAnchor: [tam / 2, tam / 2] }),
                zIndexOffset: sel ? 1000 : p.apagado ? -500 : (p.z || 0),   // p.z: lo que pide atención, arriba (2-nov-2026)
                keyboard: false,
            });
            // punto.titulo: nombre al pasar (o fijo, con tituloFijo) — 23-oct-2026, ruta de la empresa
            if (p.titulo) m.bindTooltip(String(p.titulo), { direction: 'top', offset: [0, -tam / 2], permanent: !!p.tituloFijo, className: 'dlt-tip', opacity: 1 });
            m.on('click', (e) => { L.DomEvent.stopPropagation(e); clickPuntoRef.current && clickPuntoRef.current(p); });
            capa.addLayer(m);
        });
    };
    dibujarRef.current = dibujar;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { dibujar(); }, [puntos, seleccionado, agrupar]);

    // Recorrido y anillos de distancia
    useEffect(() => {
        const capa = extrasRef.current;
        if (!capa) return;
        capa.clearLayers();
        (circulos || []).forEach(c => L.circle([c.lat, c.lng], {
            radius: c.km * 1000, color: c.color || '#64748B', weight: 1, dashArray: '4 4', fillOpacity: 0.03, interactive: false,
        }).addTo(capa));
        // lineas: un recorrido [[lat, lng], ...] o varios [{ coords: [[lat, lng], ...], color }]
        const recorridos = !lineas ? [] : Array.isArray(lineas[0]) ? [{ coords: lineas, color: '#C9341F' }] : lineas;
        recorridos.filter(r => r.coords && r.coords.length > 1).forEach(r =>
            L.polyline(r.coords, { color: r.color || '#C9341F', weight: 3, opacity: 0.75, dashArray: r.punteada ? '6 6' : null, interactive: false }).addTo(capa));
    }, [lineas, circulos]);

    // Encuadrar los puntos cuando cambia lo que se muestra. Se deja afuera algún punto suelto muy
    // lejano (uno en Luján no tiene que achicar todo CABA) y se recalcula el tamaño del contenedor
    // antes, porque en el celular el mapa a veces se encuadraba antes de tener su alto final.
    const encuadrar = () => {
        const mapa = mapaRef.current;
        if (!mapa) return;
        mapa.invalidateSize();
        const conPos = puntos.filter(p => p.lat != null && p.lng != null && !p.apagado);
        if (conPos.length === 1) { mapa.setView([conPos[0].lat, conPos[0].lng], 15); return; }
        if (conPos.length < 2) return;
        const med = (xs) => { const o = [...xs].sort((a, b) => a - b); return o[Math.floor(o.length / 2)]; };
        const c = { lat: med(conPos.map(p => p.lat)), lng: med(conPos.map(p => p.lng)) };
        const dist = p => Math.hypot((p.lat - c.lat) * 111, (p.lng - c.lng) * 92);
        const ds = conPos.map(dist).sort((a, b) => a - b);
        const limite = Math.max(15, ds[Math.floor(ds.length * 0.9)] * 1.5);
        const dentro = conPos.length > 5 ? conPos.filter(p => dist(p) <= limite) : conPos;
        mapa.fitBounds(L.latLngBounds(dentro.map(p => [p.lat, p.lng])).pad(0.1), { maxZoom: 15 });
    };
    const encuadrarRef = useRef(encuadrar);
    encuadrarRef.current = encuadrar;
    useEffect(() => {
        encuadrar();
        const t = setTimeout(() => encuadrarRef.current(), 350);   // por si el alto cambió al terminar de armarse la pantalla
        return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ajustarKey]);

    // Centrar en el seleccionado (y acercarse, si vino del buscador)
    useEffect(() => {
        const mapa = mapaRef.current;
        const p = puntos.find(x => x.id === seleccionado);
        if (!mapa || !p || p.lat == null) return;
        if (acercar) mapa.setView([p.lat, p.lng], Math.max(mapa.getZoom(), 15), { animate: true });
        else mapa.panTo([p.lat, p.lng], { animate: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seleccionado, acercar]);

    return <div ref={divRef} className={`mapa-dlt ${className}`} />;
}

export const linkGps = (direccion, lat, lng) => lat != null && lng != null
    ? `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
    : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(direccionParaMaps(direccion))}`;

// Ruta del día en Google Maps: paradas en orden (Google acepta hasta 9 intermedias), con coordenadas cuando
// se conocen (25-oct-2026: con el texto Google se iba a cualquier lado). origen: {lat, lng} de donde sale.
export function linkRuta(paradas, origen = null) {
    const ps = paradas.filter(p => p.lat != null || p.direccion).map(p => (p.lat != null ? `${p.lat},${p.lng}` : direccionParaMaps(p.direccion)));
    if (!ps.length) return null;
    const destino = ps[ps.length - 1];
    const intermedias = ps.slice(0, -1).slice(0, 9);
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destino)}`
        + (origen?.lat != null ? `&origin=${origen.lat},${origen.lng}` : '')
        + (intermedias.length ? `&waypoints=${encodeURIComponent(intermedias.join('|'))}` : '');
}

// Waze (29-oct-2026): no acepta varias paradas en un link, así que lleva a una sola: la próxima.
// Con coordenadas va directo; con la dirección, Waze la busca.
export const linkWaze = (direccion, lat, lng) => (lat != null && lng != null
    ? `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`
    : `https://waze.com/ul?q=${encodeURIComponent(`${direccionParaMaps(direccion)}, Buenos Aires, Argentina`)}&navigate=yes`);
